import * as _ from "lodash";
import {
    ICell,
    ICellMetric,
    ICellMetricAggregatorCalcLevel,
    ICubePerspective,
    ICubeSource,
} from "./PA.HyperRectangle.IF";
import { Allocator } from "../PA.Core/Basic/Allocator";
import { isPromise } from "../PA.Core/Basic/ASync";
import { NumberUtils } from "../PA.Core/Basic/Numbers";

export interface ICellMetricCalcPass {
    Pass: number;
    Level: ICellMetricAggregatorCalcLevel;
    Metrics: string[];
}

export class CellMetricUtils {
    //-- Copy Down, constant value
    // --- For each leaf,  set value = New Value
    static async setValueCopyDown(
        perspective: ICubePerspective,
        leafCells: ICell[],
        tgtMetricId: string,
        newValue: number,
    ) {
        let aTgtMetric = perspective.getCellMetric(tgtMetricId);
        if (_.isFunction(aTgtMetric.LeafWriteOvrd)) {
            for (let aLeafCell of leafCells) {
                await aTgtMetric.LeafWriteOvrd(aLeafCell, newValue);
            }
        }
        // Copy down value
        for (let aLeafCell of leafCells) {
            await this.setLeafCellPerspectiveValue(perspective, aLeafCell, tgtMetricId, newValue);
        }
    }

    //-- Scale Down, by Factor determined by New Value
    // --- For each leaf,  multiply by  (New Value / Old Value)
    static async setValueScaleBy(
        perspective: ICubePerspective,
        leafCells: ICell[],
        tgtMetricId: string,
        existingValue: number,
        newValue: number,
    ) {
        if (existingValue <= 0 || !_.isNumber(existingValue)) return;

        let scaleFactor = newValue / existingValue;

        // Scale Leaf Cells by Factor, no need for UOM conversion
        for (let aLeafCell of leafCells) {
            let aValue = _.toNumber(aLeafCell.getCellMetricValue(tgtMetricId));
            if (!_.isNumber(aValue)) continue;
            aLeafCell.setCellMetricValue(tgtMetricId, aValue * scaleFactor);
        }
    }

    static async getLeafCellPerspectiveValue(
        perspective: ICubePerspective,
        leafCell: ICell,
        metricId: string,
    ): Promise<number | string> {
        let uom = perspective.getCellMetricUOM(metricId);
        if (_.isUndefined(uom)) return leafCell.getCellMetricValue(metricId);

        if (!_.isFunction(leafCell.getUOMConvertOptions)) return leafCell.getCellMetricValue(metricId);

        let conversions = perspective.getCellMetricConversions(metricId);
        if (_.isUndefined(conversions)) return leafCell.getCellMetricValue(metricId);

        let convertOptions = leafCell.getUOMConvertOptions(metricId);

        let aConversionFactor_N = 1;

        for (let aConv of conversions.NumeratorConversions) {
            let convRate = aConv.converter.conversionRate(aConv.fromUOM, aConv.toUOM, convertOptions);
            if (isPromise(convRate)) convRate = await convRate;

            if (_.isUndefined(convRate) || _.isNaN(convRate)) {
                // Error determining Conversion rate
                aConv.errorCount |= 0;
                aConv.errorCount++;

                let errMsg = `Unable to convert ${aConv?.convertedId}`;
                //TODO Index, Increment Message Provider Error Count
                aConversionFactor_N = undefined;
                break;
            }

            aConversionFactor_N *= convRate;
        }

        let aConversionFactor_D = 1;
        for (let aConv of conversions.DenominatorConversions) {
            let convRate = aConv.converter.conversionRate(aConv.fromUOM, aConv.toUOM, convertOptions);
            if (isPromise(convRate)) convRate = await convRate;

            if (_.isUndefined(convRate) || _.isNaN(convRate)) {
                // Error determining Conversion rate
                aConv.errorCount |= 0;
                aConv.errorCount++;

                let errMsg = `Unable to convert ${aConv?.convertedId}`;
                //TODO Index, Increment Message Provider Error Count
                aConversionFactor_D = undefined;
                break;
            }

            aConversionFactor_D *= convRate;
        }

        // Determine combined conversion factor
        if (_.isNumber(aConversionFactor_N) && _.isNumber(aConversionFactor_D) && aConversionFactor_D != 0) {
            let aFactor = aConversionFactor_N / aConversionFactor_D;
            return <number>leafCell.getCellMetricValue(metricId) * aFactor;
        } else {
            //TODO Index, Increment Message Provider Error Count
            let errMsg = `Unable to convert, Conversion Rate NAN}`;
            return undefined;
        }
    }

    static async setLeafCellPerspectiveValue(
        perspective: ICubePerspective,
        leafCell: ICell,
        metricId: string,
        value: number,
    ) {
        let uom = perspective.getCellMetricUOM(metricId);
        if (_.isUndefined(uom)) {
            leafCell.setCellMetricValue(metricId, value);
            return;
        }

        if (!_.isFunction(leafCell.getUOMConvertOptions)) {
            leafCell.setCellMetricValue(metricId, value);
            return;
        }

        let conversions = perspective.getCellMetricConversions(metricId);
        if (_.isUndefined(conversions)) {
            leafCell.setCellMetricValue(metricId, value);
            return;
        }

        let convertOptions = leafCell.getUOMConvertOptions(metricId);

        // Determine the reverse conversion
        let aConversionFactor_N = 1;
        for (let aConv of _.reverse(conversions.NumeratorConversions)) {
            let convRate = aConv.converter.conversionRate(aConv.toUOM, aConv.fromUOM, convertOptions);
            if (isPromise(convRate)) convRate = await convRate;

            if (_.isUndefined(convRate) || _.isNaN(convRate)) {
                // Error determining Conversion rate
                aConv.errorCount |= 0;
                aConv.errorCount++;

                let errMsg = `Unable to convert ${aConv?.convertedId}`;
                //TODO Index, Increment Message Provider Error Count
                aConversionFactor_N = undefined;
                break;
            }

            aConversionFactor_N *= convRate;
        }

        // Determine the reverse conversion
        let aConversionFactor_D = 1;
        for (let aConv of _.reverse(conversions.DenominatorConversions)) {
            let convRate = aConv.converter.conversionRate(aConv.toUOM, aConv.fromUOM, convertOptions);
            if (isPromise(convRate)) convRate = await convRate;

            if (_.isUndefined(convRate) || _.isNaN(convRate)) {
                // Error determining Conversion rate
                aConv.errorCount |= 0;
                aConv.errorCount++;

                let errMsg = `Unable to convert ${aConv?.convertedId}`;
                //TODO Index, Increment Message Provider Error Count
                aConversionFactor_D = undefined;
                break;
            }

            aConversionFactor_D *= convRate;
        }

        // Determine combined conversion factor
        if (_.isNumber(aConversionFactor_N) && _.isNumber(aConversionFactor_D) && aConversionFactor_D != 0) {
            let aFactor = aConversionFactor_N / aConversionFactor_D;
            leafCell.setCellMetricValue(metricId, value * aFactor);
        } else {
            //TODO Index, Increment Message Provider Error Count
            let errMsg = `Unable to convert, Conversion Rate NAN}`;
            return;
        }
    }

    //-- Reallocate using existing value
    //--- Allocate newValue across existing (non-zero values)
    static async setValueReallocate(
        perspective: ICubePerspective,
        leafCells: ICell[],
        tgtMetricId: string,
        newValue: number,
    ) {
        await CellMetricUtils.setValueAllocateBy(perspective, leafCells, tgtMetricId, tgtMetricId, newValue);
    }

    //-- Reallocate using auxiliary value, falls back to even allocation if sum = 0
    //--- Allocate newValue across a specified metric
    static async setValueAllocateBy(
        perspective: ICubePerspective,
        leafCells: ICell[],
        tgtMetricId: string,
        allcByMetricId: string,
        newValue: number,
    ) {
        const self = this;

        // Do simple case, zero
        if (newValue == 0) {
            _.forEach(leafCells, (aLeafCell) => {
                aLeafCell.setCellMetricValue(tgtMetricId, 0);
            });
            return;
        }

        // Do the allocation
        let metricAllocator = new Allocator();
        let evenAllocator = new Allocator();

        //--
        for (let aLeafCell of leafCells) {
            let allocatorValue = _.toNumber(
                await self.getLeafCellPerspectiveValue(perspective, aLeafCell, allcByMetricId),
            );
            if (!_.isNumber(allocatorValue)) allocatorValue = 0;

            metricAllocator.add(aLeafCell.CellId, allocatorValue);
            evenAllocator.add(aLeafCell.CellId, 1);
        }

        // determine allocator to use
        let chosenAllocation: Allocator = undefined;
        if (metricAllocator.sumTotal > 0) chosenAllocation = metricAllocator;
        else chosenAllocation = evenAllocator;

        for (let aLeafCell of leafCells) {
            let aPcnt = chosenAllocation.getPercent(aLeafCell.CellId);
            await self.setLeafCellPerspectiveValue(
                perspective,
                aLeafCell,
                tgtMetricId,
                NumberUtils.round(newValue * aPcnt, 6),
            );
        }
    }

    //-- Create Metric Calc Ordering
    static getCalcPasses(cubeSource: ICubeSource, metricIds: string[]): ICellMetricCalcPass[] {
        if (_.isEmpty(cubeSource)) return [];

        //-- Create WriteBack dependencies
        let dctWriteBackDependencies: { [metricId: string]: { [metricIdDep: string]: string } } = {};

        //-- Find Dependent Metrics, look for children and peers
        let dctMetrics: { [id: string]: ICellMetric } = {};
        let dctMissingMetrics: { [id: string]: boolean } = {};

        function addMetric(aMetricId: string) {
            if (aMetricId in dctMetrics) return;
            if (aMetricId in dctMissingMetrics) return;
            let metric = cubeSource.getMetric(aMetricId);
            if (_.isEmpty(metric)) {
                dctMissingMetrics[aMetricId] = true;
                return;
            }

            dctMetrics[metric.MetricId] = metric;

            for (let writeBackMetricId of metric.RefreshMetricIdsOnRead || []) {
                if (!(writeBackMetricId in dctWriteBackDependencies)) dctWriteBackDependencies[writeBackMetricId] = {};
                dctWriteBackDependencies[writeBackMetricId][metric.MetricId] = metric.MetricId;
                addMetric(writeBackMetricId);
            }

            for (let depMetricId of metric.SupportingMetricsIds) {
                addMetric(depMetricId);
            }
        }

        for (let metricId of metricIds) {
            addMetric(metricId);
        }

        let arrMetricsAll = _.values(dctMetrics);
        let arrMetricsLeafOnly = _.filter(arrMetricsAll, (a) => a.bIsLeafLevelOnly);
        let arrMetricsToOrder = _.filter(arrMetricsAll, (a) => !a.bIsLeafLevelOnly);
        let arrMetricsLeafCalc = _.filter(
            arrMetricsToOrder,
            (a) => a.AggregatorCalcLevel == ICellMetricAggregatorCalcLevel.Leaf,
        );
        let arrMetricsPerspectiveCalc = _.filter(
            arrMetricsToOrder,
            (a) => a.AggregatorCalcLevel == ICellMetricAggregatorCalcLevel.Perspective,
        );

        let dctMetricsProcessed: { [id: string]: boolean } = {};
        //-- All Leaf columns that aren't write back targets, are processed
        _.forEach(arrMetricsLeafOnly, (a) => {
            if (a.MetricId in dctWriteBackDependencies) arrMetricsLeafCalc.push(a);
            else dctMetricsProcessed[a.MetricId] = true;
        });

        let passCount = 1;
        let rv: ICellMetricCalcPass[] = [];

        function addToPassNextPass(arr: ICellMetric[], level: ICellMetricAggregatorCalcLevel) {
            if (_.isEmpty(arr)) return;
            let arrNextPass: ICellMetric[] = [];
            let arrCurrPass: ICellMetric[] = [];

            // Find all metrics without dependents, or with dependents in previous passes.
            for (let aMetric of arr) {
                // Check Supporting Metrics and any Writeback metrics
                let allDeps = _.union(
                    aMetric.SupportingMetricsIds,
                    _.values(dctWriteBackDependencies[aMetric.MetricId] || {}),
                );
                if (_.isEmpty(allDeps)) {
                    arrCurrPass.push(aMetric);
                } else {
                    // If all the dependents are in a previous pass, then add
                    let bAdd = true;
                    _.forEach(allDeps, (aDepMtrcId) => {
                        if (aDepMtrcId in dctMissingMetrics) return;

                        if (aDepMtrcId in dctMetricsProcessed) return;
                        bAdd = false;
                        return false;
                    });

                    if (bAdd) arrCurrPass.push(aMetric);
                    else arrNextPass.push(aMetric);
                }
            } // Next Metric to check

            if (_.isEmpty(arrCurrPass)) {
                // This is bad, that means there is a gap that isn't resolving
                return;
            }

            let pass: ICellMetricCalcPass = {
                Pass: passCount,
                Level: level,
                Metrics: _.map(arrCurrPass, (a) => a.MetricId),
            };
            passCount += 1;
            rv.push(pass);

            _.forEach(arrCurrPass, (a) => {
                dctMetricsProcessed[a.MetricId] = true;
            });

            // Recurse
            if (!_.isEmpty(arrNextPass)) addToPassNextPass(arrNextPass, level);
        }

        addToPassNextPass(arrMetricsLeafCalc, ICellMetricAggregatorCalcLevel.Leaf);
        addToPassNextPass(arrMetricsPerspectiveCalc, ICellMetricAggregatorCalcLevel.Perspective);

        return rv;
    }

    //-- Create Refresh Tree

    static getRefreshMetricParentsByMetricId(cubeSource: ICubeSource): { [metricId: string]: string[] } {
        if (_.isEmpty(cubeSource)) return {};

        interface dctEntry {
            [id: string]: dctEntry;
        }

        let dctRV: dctEntry = {};

        //-- Find Dependent Metrics, look for children and peers

        function findOrCreateNode(aMetricId: string): dctEntry {
            if (aMetricId in dctRV) return dctRV[aMetricId];

            dctRV[aMetricId] = {};

            let metric = cubeSource.getMetric(aMetricId);

            if (_.isEmpty(metric)) return dctRV[aMetricId];

            for (let targetMetricId of metric.RefreshMetricIdsOnRead || []) {
                if (targetMetricId == aMetricId) continue;

                let wbTarget = findOrCreateNode(targetMetricId);
                dctRV[aMetricId][targetMetricId] = wbTarget;
            }

            for (let depMetricId of metric.SupportingMetricsIds) {
                if (aMetricId == depMetricId) continue;
                if (_.isEmpty(depMetricId)) continue;

                // Create a parent for me
                let dm = findOrCreateNode(depMetricId);
                dm[aMetricId] = dctRV[aMetricId];

                // Create a parent for any writeback metric
                // Handle case when metric is a writeback metric for a row level calc
                // i,e, base volume build row level calc
                for (let wbMetricId of metric.RefreshMetricIdsOnRead || []) {
                    if (wbMetricId == depMetricId) continue;

                    let wb = findOrCreateNode(wbMetricId);
                    dm[wbMetricId] = wb;
                }
            }

            // Handle case when metric is updated by leaf writeback perspective metric
            //i.e. updating % discount triggers write to Promoted Price
            for (let wbMetricId of metric.RefreshMetricIdsOnWrite || []) {
                if (wbMetricId == aMetricId) continue;

                let wb = findOrCreateNode(wbMetricId);
                wb[aMetricId] = dctRV[aMetricId];
            }

            return dctRV[aMetricId];
        }

        // Gets Visible Metrics
        let metrics = cubeSource.getMetrics({ bIncludeWriteBack: true });
        _.forEach(metrics, (aMetric) => {
            findOrCreateNode(aMetric.MetricId);
        });

        let rv: { [metricId: string]: string[] } = {};
        _.forEach(dctRV, (value, key) => {
            let dct: { [id: string]: string } = {};

            function addMetricsRecurse(aMetricId: string) {
                if (aMetricId in dct) return;

                if (!(aMetricId in dctRV)) return;

                dct[aMetricId] = aMetricId;
                let aDct = dctRV[aMetricId];
                _.forEach(aDct, (value, key) => {
                    addMetricsRecurse(key);
                });
            }

            addMetricsRecurse(key);

            rv[key] = _.values(dct);
        });

        return rv;
    }

    static formatMetricValue(CellMetric: ICellMetric, aValue: number): string {
        let cellFormatStr: string;
        if (CellMetric?.RenderOptions?.showThousandsSeperator) cellFormatStr = "#,##0";
        else cellFormatStr = "#0";

        const decimalPlaces = CellMetric?.RenderOptions?.decimalPlaces || 2;
        if (decimalPlaces > 0) {
            cellFormatStr = `${cellFormatStr}.${"0".repeat(decimalPlaces)}`;
        }

        return NumberUtils.msformat(cellFormatStr, aValue);
    }
}
